import React, {useEffect, useState} from "react";
import Filters from "./components/filters/filters";
import './index.scss';
import {useDispatch, useSelector} from "react-redux";
import * as routes from "../../constants/routes";
import BreadCrumbs from "../../components/common/bread_crumbs";
import TopBanner from "../category_page/top_banner";
import SearchInput from "./components/SearchInput/SearchInput";
import {getMediaPath, reactParserCommonOptions} from "../../app/functions";
import ReactHtmlParser from "react-html-parser";
import Results from "./components/results";
import {searchApi, useGetSearchFiltersQuery} from "../../redux/api/search.api";

function debounce(func, timeout) {
    let timer;

    return (...args) => {
        clearTimeout(timer);

        timer = setTimeout(() => {
            func.apply(this, args);
        }, timeout);
    };
}

const labelSorter = (a, b) => a.label > b.label ? 1 : -1

const initialFields = ['city_id', 'category', 'benefit_title'];
const fields = ['benefit_title'];
const baseFilters = {benefit_type: 100}
const filtersArrConfig = [
        {
            isSearchable: true,
            isMulti: true,
            isRtl: true,
            placeholder: 'city_id',
            placeholderText: '',
            getOptions: (rawOptions, gd) => {
                // Map all cities to their corresponding labels
                const citiesMap = gd.citiesArr.reduce((cities, city) => {
                    cities[city.id] = city.text;
                    return cities;
                }, {});

                return rawOptions.filter(c => c && citiesMap[c]).map(c => ({
                    label: citiesMap[c],
                    value: c
                })).sort(labelSorter)

            }
        },
        {
            isSearchable: true,
            isMulti: true,
            isRtl: true,
            placeholder: 'category',
            placeholderText: '',
            getOptions: (rawOptions, gd) => {
                const cats = gd.categoriesArr.Options.reduce((cats, c) => {
                    cats[String(c.id)] = c.text
                    return cats
                }, {})

                return  rawOptions.filter(cat => cats[String(cat)]).map(cat => (
                    {
                        label: cats[String(cat)],  // Ensure label is set correctly
                        value: cat
                    })).sort(labelSorter)
            }
        },
        {
            isSearchable: true,
            isMulti: true,
            isRtl: true,
            placeholder: 'sub_category',
            dataKey: 'category',
            placeholderText: '',
            getOptions: (rawOptions, gd, selectedFilters) => {
                let arr = [];
                const map = new Map();
                if (selectedFilters.category?.length){
                    rawOptions = selectedFilters.category.flatMap(c=>gd.categoriesArr.SubCategories[c]).map(c=>c.id)
                }

                Object.values(gd.categoriesArr.SubCategories).forEach(category => {
                    category.forEach(sub => {
                        if (!map.has(sub.id)) {
                            map.set(sub.id, sub.text)
                        }
                    })
                })
                for (const id of rawOptions) {
                    if (map.has(Number(id))) {
                        arr.push({label: map.get(Number(id)), value: Number(id)})
                    }
                }
                return arr.sort(labelSorter);
            }

        },
        {
            isSearchable: true,
            isMulti:
                false,
            isRtl:
                true,
            placeholder:
                'benefit_title',
            placeholderText:
                '',
            getOptions:
                (rawOptions, gd) => {
                    //benefit title and benefit id are the same
                    return rawOptions.sort((a, b) => a > b ? 1 : -1).filter(Boolean).map((i) => ({label: i, value: i}));
                }
        }
        ,
    ]
;

const Search = () => {
    const [searchResults, setSearchResult] = useState({});
    const gd = useSelector((store) => store.config);
    const [config, setConfig] = useState(filtersArrConfig);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({})
    const [dirty, setDirty] = useState(false);

    const {data: searchFilters} = useGetSearchFiltersQuery({
        fields: initialFields,
        filters: baseFilters
    })

    const dispatch = useDispatch();

    const translations = gd.Translations;
    const benefitType = gd.BenefitTypes[100];

    useEffect(() => {
        dirty && debounce(() => {
            getBenefits(1)
        }, 300)()
    }, [filters]);

    const getBenefits = async (page) => {
        const {query = '', category=[], sub_category = [], benefit_title, city_id = []} = filters;

        const searchParams = {
            query: query || undefined,
            page: page || 1,
            filters: {
                ...baseFilters,
                benefit_title,
                category: [...category,...category?.flatMap(cat => gd.categoriesArr.SubCategories[cat]?.map(c => String(c.id)))].sort(),
                sub_category: sub_category.sort(),
                city_id: city_id.sort(),
            },
        }

        setLoading(true)
        const {data} = await dispatch(searchApi.endpoints.searchBenefits.initiate({
            ...searchParams,
            fields: fields
        }, {skip: !dirty, forceRefetch: true}))

        const {records, metadata} = data.benefits;
        if (records) {
            const activeBenefits = records.filter(record => (
                record.active === true &&
                record.is_expired === false &&
                record?.imagePath?.trim?.()
            ));

            setSearchResult((prevBenefits) => ({
                records: metadata.currentPage > 1 ? [...prevBenefits.records, ...activeBenefits] : [...activeBenefits],
                metadata: metadata,
            }));
            setLoading(false)
        }
        if (metadata?.filters) {
            const filters = metadata.filters
            updateFiltersValues({
                ...searchFilters,
                ...filters.reduce((agg, f) => {
                    agg[f.inputName] = f.values
                    return agg
                }, {})
            })

        }

    }


    const updateFiltersValues = (fieldOptions) => {
        setConfig((prevConfig) =>
            prevConfig.map((f) => {
                const filter = fieldOptions?.[f.placeholder] || fieldOptions?.[f.dataKey]

                if (filter) {
                    return {
                        ...f,
                        placeholderText: translations['_' + f.placeholder + '_filter'],
                        options: f.getOptions([...filter], gd, filters),
                    };
                }
                return f;
            })
        );
    }

    useEffect(() => {
        searchFilters && updateFiltersValues(searchFilters)
    }, [searchFilters]);

    const onSelect = (key, value) => {
        setDirty(true)
        setFilters(filters => ({
            ...filters,
            [key]: value
        }))
    };

    const handleScroll = (isBottom)=> {
        if(isBottom && searchResults.metadata.totalLeftPages){
            getBenefits(searchResults.metadata.currentPage+1)
        }
    }

    function getBreadCrumbs() {
        return [
            {
                text: translations['search_discount_debit'],
                route: routes.search,
            },
        ];
    }

    return (
        <main>
            <TopBanner categoryName={translations['search_discount_debit']}/>
            <div className='filters-container'>
                <BreadCrumbs
                    root={{
                        text: 'דף הבית',
                        route: routes.home,
                    }}
                    crumbs={getBreadCrumbs()}
                    className="bread-crumbs"
                />
                <img src={getMediaPath(benefitType.searchPageBanner)} alt="באנר" className="category-main-banner"/>
                <>
                    {!dirty && ReactHtmlParser(benefitType.search_page_text, reactParserCommonOptions())}
                </>
                <Filters filtersArrConfig={config} setValue={onSelect}/>
                <div style={{border: "1px solid lightgrey", width: '100%'}}></div>
                <SearchInput
                    setQuery={(query) => onSelect('query', query)}
                    translations={translations}
                />

                {dirty && searchResults?.records &&
                    <Results translations={translations}
                             data={searchResults}
                             loading={loading}
                             onScroll={(e)=>handleScroll(e)}
                    />
                }
            </div>
        </main>
    );
};

export default Search;
